/**
 * BehavioralTab - Shows behavioral code distribution by dimension.
 *
 * Uses RadarChart from BehavioralWorkbench (reuse).
 * Fetches chunks mentioning this person via SNA search,
 * aggregates codes by dimension.
 */

import React from 'react';
import { Box, Typography, Chip, CircularProgress, Alert, Divider, Button } from '@mui/material';
// NOTE: Platform constants - consider extracting to shared/constants/
import { FAANA_DIMENSIONS, DIMENSION_COLORS } from '../../../BehavioralWorkbench/constants/workbenchConfig';
import RadarChart from '../../../BehavioralWorkbench/components/Charts/RadarChart';
import usePersonProfile from '../../hooks/usePersonProfile';
import EvidenceChunks from './EvidenceChunks';

// eslint-disable-next-line max-lines-per-function
function BehavioralTab({ node, entityId }) {
  const {
    dimensionScores,
    topCodes,
    evidence,
    loading,
    error,
    retry,
  } = usePersonProfile(entityId, node.id);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert
          severity="error"
          action={<Button color="inherit" size="small" onClick={retry}>Retry</Button>}
        >
          Failed to load behavioral data
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      {/* Radar Chart - Dimension Distribution */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Dimension Distribution
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
        <RadarChart
          dimensions={dimensionScores}
          size={200}
          showLabels
          showPoints
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* Top Behavioral Codes */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Top Behavioral Codes
      </Typography>
      {topCodes.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No behavioral codes found for this person
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mb: 2 }}>
          {topCodes.slice(0, 12).map((code) => (
            <Chip
              key={code.name}
              label={`${code.name} (${code.count})`}
              size="small"
              sx={{
                bgcolor: code.valence === 'positive'
                  ? 'rgba(35, 163, 143, 0.1)'
                  : code.valence === 'negative'
                    ? 'rgba(255, 55, 73, 0.1)'
                    : 'rgba(0, 151, 178, 0.1)',
                borderColor: code.valence === 'positive'
                  ? '#23A38F'
                  : code.valence === 'negative'
                    ? '#FF3749'
                    : '#0097B2',
                border: '1px solid',
                fontSize: '0.7rem',
                height: 24,
              }}
            />
          ))}
        </Box>
      )}

      {/* Dimension Breakdown */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        By Dimension
      </Typography>
      {FAANA_DIMENSIONS.map((dim) => {
        const score = dimensionScores[dim] || 0;
        return (
          <Box key={dim} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: DIMENSION_COLORS[dim],
                flexShrink: 0,
              }}
            />
            <Typography variant="body2" sx={{ flex: 1, fontSize: '0.8rem' }}>
              {dim}
            </Typography>
            <Box
              sx={{
                flex: 2,
                height: 6,
                bgcolor: 'grey.100',
                borderRadius: 1,
                overflow: 'hidden',
              }}
            >
              <Box
                sx={{
                  width: `${score}%`,
                  height: '100%',
                  bgcolor: DIMENSION_COLORS[dim],
                  borderRadius: 1,
                  transition: 'width 0.5s ease',
                }}
              />
            </Box>
            <Typography variant="caption" sx={{ width: 30, textAlign: 'right' }}>
              {Math.round(score)}
            </Typography>
          </Box>
        );
      })}

      <Divider sx={{ my: 2 }} />

      {/* Evidence */}
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
        Evidence ({evidence.length})
      </Typography>
      <EvidenceChunks chunks={evidence} />
    </Box>
  );
}

export default BehavioralTab;
